/**
 * Redirect Validation Utilities
 * Provides whitelist-based validation for redirect paths to prevent open redirect attacks
 *
 * Security Features:
 * - Whitelist-based validation (not blacklist)
 * - Prevents absolute URLs and protocol-relative URLs
 * - Sanitizes organization slugs from localStorage
 * - Validates against allowed path patterns
 *
 * OWASP: A01:2021 - Broken Access Control
 * Mitigates: Open redirect attacks, phishing via malicious redirects
 */

import { log } from '../errors/logger';

/**
 * Allowed redirect path patterns
 * Only paths matching these patterns are allowed for redirects
 * Uses whitelist approach for maximum security
 */
const ALLOWED_REDIRECT_PATTERNS: RegExp[] = [
  // Login routes
  /^\/login$/,
  /^\/login\/slug\/[\w-]+$/,  // Organization-specific login

  // Main app routes
  // Dashboard deaktiviert - Chat ist Startseite
  // /^\/dashboard$/,
  /^\/documents$/,
  /^\/chat$/,
  /^\/chat\/[0-9a-fA-F]{24}$/,  // Chat route with conversationId (MongoDB ObjectId)
  /^\/settings$/,
  /^\/profile$/,

  // Document routes
  /^\/document\/\d+$/,
  /^\/document\/[\w-]+$/,

  // Home/root
  /^\/$/,
];

/**
 * Validate if a redirect path is allowed
 * Uses whitelist approach to prevent open redirect vulnerabilities
 *
 * Security checks:
 * 1. Must be relative path (starts with /)
 * 2. Must not be protocol-relative (starts with //)
 * 3. Must not contain protocol (://)
 * 4. Must match one of the allowed patterns
 *
 * @param path - Redirect path to validate
 * @returns true if path is allowed, false if blocked
 *
 * @example
 * isAllowedRedirect('/chat')                   // true
 * isAllowedRedirect('/login/slug/acme')        // true
 * isAllowedRedirect('//evil.com')              // false (protocol-relative)
 * isAllowedRedirect('https://evil.com')        // false (absolute URL)
 * isAllowedRedirect('/admin/../../../etc')     // false (not in whitelist)
 */
export const isAllowedRedirect = (path: string): boolean => {
  if (!path) {
    return false;
  }

  const trimmedPath = path.trim();

  // SECURITY: Must be relative path (starts with /)
  if (!trimmedPath.startsWith('/')) {
    log.warn('[Redirect Validation] Blocked non-relative path', {
      path: trimmedPath.substring(0, 50)
    });
    return false;
  }

  // SECURITY: Prevent protocol-relative URLs (//evil.com)
  if (trimmedPath.startsWith('//')) {
    log.warn('[Redirect Validation] Blocked protocol-relative URL', {
      path: trimmedPath.substring(0, 50)
    });
    return false;
  }

  // SECURITY: Prevent absolute URLs (https://evil.com)
  if (trimmedPath.includes('://')) {
    log.warn('[Redirect Validation] Blocked absolute URL', {
      path: trimmedPath.substring(0, 50)
    });
    return false;
  }

  // SECURITY: Check against whitelist
  const isAllowed = ALLOWED_REDIRECT_PATTERNS.some(pattern =>
    pattern.test(trimmedPath)
  );

  if (!isAllowed) {
    log.warn('[Redirect Validation] Path not in whitelist', {
      path: trimmedPath.substring(0, 50)
    });
    return false;
  }

  return true;
};

/**
 * Sanitize organization slug from user input
 * Only allows alphanumeric characters, hyphens, and underscores
 * Prevents injection attacks via malicious slugs
 *
 * @param slug - Organization slug to sanitize
 * @returns sanitized slug with only safe characters
 *
 * @example
 * sanitizeSlug('acme-corp')        // 'acme-corp'
 * sanitizeSlug('acme_corp_123')    // 'acme_corp_123'
 * sanitizeSlug('acme/../admin')    // 'acmeadmin' (dangerous chars removed)
 * sanitizeSlug('<script>alert</script>')  // 'scriptalertscript'
 */
export const sanitizeSlug = (slug: string): string => {
  if (!slug) {
    return '';
  }

  // Only allow alphanumeric, dash, underscore
  // This prevents:
  // - Path traversal (../)
  // - Special characters that could break URLs
  // - XSS attempts
  return slug.replace(/[^a-zA-Z0-9_-]/g, '');
};

/**
 * Get safe redirect path from localStorage
 * Retrieves organization context and builds redirect path with validation
 *
 * Security flow:
 * 1. Try to read organization from localStorage
 * 2. Sanitize organization slug
 * 3. Build redirect path
 * 4. Validate against whitelist
 * 5. Fall back to /login if any step fails
 *
 * @returns safe redirect path or default /login
 *
 * @example
 * // With valid org in localStorage
 * getRedirectPath()  // '/login/slug/acme-corp'
 *
 * // With invalid/missing org
 * getRedirectPath()  // '/login'
 */
export const getRedirectPath = (): string => {
  const defaultPath = '/login';

  try {
    // Try to get organization context from localStorage
    const orgContext = window.localStorage.getItem('current_organization');

    if (!orgContext) {
      log.debug('[Redirect Validation] No organization context found');
      return defaultPath;
    }

    // Parse organization data
    let org: { slug?: string };
    try {
      org = JSON.parse(orgContext);
    } catch (parseError) {
      log.warn('[Redirect Validation] Failed to parse organization context', {
        error: parseError
      });
      return defaultPath;
    }

    // Check if org has slug
    if (!org.slug) {
      log.debug('[Redirect Validation] Organization has no slug');
      return defaultPath;
    }

    // SECURITY: Sanitize slug to prevent injection
    const sanitizedSlug = sanitizeSlug(org.slug);

    if (!sanitizedSlug) {
      log.warn('[Redirect Validation] Invalid organization slug after sanitization', {
        originalSlug: org.slug.substring(0, 20)
      });
      return defaultPath;
    }

    // Build redirect path
    const redirectPath = `/login/slug/${sanitizedSlug}`;

    // SECURITY: Validate against whitelist
    if (!isAllowedRedirect(redirectPath)) {
      log.warn('[Redirect Validation] Built redirect path not allowed', {
        redirectPath
      });
      return defaultPath;
    }

    log.debug('[Redirect Validation] Using organization-specific redirect', {
      slug: sanitizedSlug,
      redirectPath
    });

    return redirectPath;

  } catch (error) {
    // Catch any unexpected errors
    log.error('[Redirect Validation] Error getting redirect path', {
      error
    });
    return defaultPath;
  }
};

/**
 * Validate and sanitize a redirect path from query parameters
 * Used for post-login redirects specified in URL query params
 *
 * @param redirectParam - Redirect path from query parameter
 * @returns validated path or null if invalid
 *
 * @example
 * validateRedirectParam('/chat')                // '/chat'
 * validateRedirectParam('https://evil.com')     // null
 * validateRedirectParam('//evil.com')           // null
 * validateRedirectParam('/unknown/path')        // null
 */
export const validateRedirectParam = (redirectParam: string | null): string | null => {
  if (!redirectParam) {
    return null;
  }

  const trimmed = redirectParam.trim();

  if (!isAllowedRedirect(trimmed)) {
    log.warn('[Redirect Validation] Invalid redirect parameter', {
      param: trimmed.substring(0, 50)
    });
    return null;
  }

  return trimmed;
};
